import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useTheme } from '@material-ui/core';
import RadioButtonGroupContext from '../RadioButtonGroup/RadioButtonGroupContext';
import * as Styled from './RadioButton.styled';

const getColourCodeFromVariant = (variant, theme) => {
  const map = {
    primary: theme.palette.common.primary,
    secondary: theme.palette.common.secondary,
    success: theme.palette.common.success,
    warning: theme.palette.common.warning,
    slighter: theme.palette.common.lightText,
    error: theme.palette.common.error400,
  };
  return map[variant] || map['primary'];
};

const RadioButton = (props) => {
  const {
    classes,
    className,
    checked: checkedProp,
    label,
    variant,
    onChange: onChangeProp,
    value,
    labelId,
    ...passThroughProps
  } = props;

  const radioGroupContext = useContext(RadioButtonGroupContext);

  // If this radio button is inside a radio group component it gets preference.
  const checked = radioGroupContext
    ? radioGroupContext.value === value
    : checkedProp;

  const handleOnChange = useCallback(
    (event) => {
      radioGroupContext
        ? radioGroupContext.onChange(event.target.value)
        : onChangeProp(event);
    },
    [onChangeProp, radioGroupContext]
  );

  const theme = useTheme();

  const buttonColour = getColourCodeFromVariant(variant, theme);

  return (
    <Styled.Wrapper className={classnames(className, classes.root)}>
      <Styled.ButtonContainer colour={buttonColour} className={classes.button}>
        <Styled.InvisibleInput
          type={'radio'}
          checked={checked}
          value={value}
          aria-labelledby={labelId}
          onChange={handleOnChange}
          {...passThroughProps}
        />
        {checked ? <Styled.CheckedIcon /> : <Styled.UnCheckedIcon />}
      </Styled.ButtonContainer>
      {label && (
        <Styled.ButtonLabel
          // If not string then we assume that the component will have the id attribute.
          id={typeof label === 'string' ? labelId : ''}
          className={classes.label}>
          {label}
        </Styled.ButtonLabel>
      )}
    </Styled.Wrapper>
  );
};

RadioButton.propTypes = {
  name: PropTypes.string,
  labelId: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf([
    'primary',
    'secondary',
    'error',
    'warning',
    'success',
  ]),
  onChange: PropTypes.func,
  checked: PropTypes.bool,
  className: PropTypes.string,
  classes: PropTypes.shape({
    root: PropTypes.string,
    button: PropTypes.string,
    label: PropTypes.string,
  }),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

RadioButton.defaultProps = {
  checked: false,
  disabled: false,
  variant: 'primary',
  classes: {
    root: '',
    label: '',
    button: '',
  },
  className: '',
  onChange: () => {},
  labelId: '',
};

export default RadioButton;
